/**
 * Appels et clés de cache du socle d'authentification.
 *
 * Les schémas viennent de `@afs/shared` : ce sont EXACTEMENT ceux avec
 * lesquels l'API valide. Front et back ne peuvent donc pas diverger
 * (skill `frontend-react-afs` § Formulaires).
 */
import {
  schemaActivationTotp,
  schemaCentreChoisi,
  schemaOptionPoste,
  schemaResultatConnexion,
  schemaResultatOuverture,
  schemaEtapeSuivante,
  schemaUtilisateurCourant,
  type CentreChoisi,
  type ChangementMotDePasseVolontaire,
  type DemandeReinitialisation,
  type EntreesConnexion,
  type ReinitialisationMotDePasse,
  type EtapeSuivante,
  type OptionPoste,
  type ResultatOuverture,
  type ResultatConnexion,
  type UtilisateurCourant,
} from '@afs/shared'
import { z } from 'zod'
import { requete } from '../../shared/api/client.js'

/**
 * Clés centralisées — jamais écrites à la main dans un composant. Une clé
 * recopiée ailleurs est une invalidation qui ne marchera pas
 * (skill `frontend-react-afs` § Clés de cache).
 */
export const clesAuth = {
  tout: ['auth'] as const,
  session: () => [...clesAuth.tout, 'session'] as const,
}

export function connecter(entrees: EntreesConnexion): Promise<ResultatConnexion> {
  return requete('/auth/connexion', {
    methode: 'POST',
    corps: entrees,
    schema: schemaResultatConnexion,
  })
}

/**
 * Deuxième temps de la connexion. Le jeton temporaire est présenté
 * explicitement : il n'a pas ouvert de session, il n'est donc pas dans le
 * client.
 */
export function verifierSecondFacteur(
  code: string,
  jetonTemporaire: string,
): Promise<ResultatOuverture> {
  return requete('/auth/2fa/verification', {
    methode: 'POST',
    corps: { code },
    jeton: jetonTemporaire,
    schema: schemaResultatOuverture,
  })
}

export function utilisateurCourant(): Promise<UtilisateurCourant> {
  return requete('/auth/moi', { schema: schemaUtilisateurCourant })
}

export function preparerActivationTotp(): Promise<{ uri: string; secretBase32: string }> {
  return requete('/auth/2fa/activation', { methode: 'POST', schema: schemaActivationTotp })
}

export function confirmerActivationTotp(code: string): Promise<void> {
  return requete('/auth/2fa/confirmation', { methode: 'POST', corps: { code } })
}

export function desactiverTotp(): Promise<void> {
  return requete('/auth/2fa', { methode: 'DELETE' })
}

/**
 * Changement volontaire. Ne rend rien : toutes les sessions sont fermées,
 * y compris celle qui appelle. L'écran doit donc renvoyer vers la connexion.
 */
export function changerSonMotDePasse(entrees: ChangementMotDePasseVolontaire): Promise<void> {
  return requete('/auth/mot-de-passe', { methode: 'POST', corps: entrees })
}

export function deconnecter(): Promise<void> {
  return requete('/auth/deconnexion', { methode: 'POST' })
}

// ─────────────────────────────────────────────────────────────────────
//  Première connexion — deux étapes, chacune avec son jeton
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace le mot de passe provisoire. Rend l'étape suivante — le serveur
 * décide, le front n'a rien à deviner.
 */
export function changerMotDePasseProvisoire(
  motDePasse: string,
  jetonTemporaire: string,
): Promise<EtapeSuivante> {
  return requete('/auth/premiere-connexion/mot-de-passe', {
    methode: 'POST',
    corps: { motDePasse },
    jeton: jetonTemporaire,
    schema: schemaEtapeSuivante,
  })
}

export function preparerEnrolementInitial(
  jetonTemporaire: string,
): Promise<{ uri: string; secretBase32: string }> {
  return requete('/auth/premiere-connexion/activation', {
    methode: 'POST',
    jeton: jetonTemporaire,
    schema: schemaActivationTotp,
  })
}

/** La confirmation ouvre la session, ou renvoie vers la déclaration de poste. */
export function confirmerEnrolementInitial(
  code: string,
  jetonTemporaire: string,
): Promise<ResultatOuverture> {
  return requete('/auth/premiere-connexion/confirmation', {
    methode: 'POST',
    corps: { code },
    jeton: jetonTemporaire,
    schema: schemaResultatOuverture,
  })
}

// ─────────────────────────────────────────────────────────────────────
//  Mot de passe oublié
// ─────────────────────────────────────────────────────────────────────

/**
 * Demande d'un lien. Ne rend rien, et surtout ne dit pas si l'adresse est
 * connue : l'écran affiche le même message dans tous les cas.
 */
export function demanderReinitialisation(entrees: DemandeReinitialisation): Promise<void> {
  return requete('/auth/mot-de-passe-oublie', { methode: 'POST', corps: entrees })
}

export function reinitialiserMotDePasse(
  entrees: ReinitialisationMotDePasse,
): Promise<{ compteVerrouille: boolean }> {
  return requete('/auth/mot-de-passe-oublie/reinitialisation', {
    methode: 'POST',
    corps: entrees,
    schema: z.object({ compteVerrouille: z.boolean() }),
  })
}

// ─────────────────────────────────────────────────────────────────────
//  Poste du jour
// ─────────────────────────────────────────────────────────────────────

/**
 * Les sites où prendre son poste aujourd'hui.
 *
 * Le jeton d'étape est présenté explicitement : l'identité est établie, la
 * session ne l'est pas encore, donc il n'y a rien dans le client.
 */
export function postesDuJour(jetonTemporaire: string): Promise<OptionPoste[]> {
  return requete('/auth/postes', {
    jeton: jetonTemporaire,
    schema: z.array(schemaOptionPoste),
  })
}

/** Décision 432 — le Super administrateur change de centre, sur la page ; `null` : aucun. */
export function choisirSonCentre(siteId: string | null): Promise<CentreChoisi> {
  return requete('/auth/centre', {
    methode: 'PUT',
    corps: { siteId },
    schema: schemaCentreChoisi,
  })
}

/** Déclare le poste et ouvre la session. */
export function declarerSonPoste(
  siteId: string,
  jetonTemporaire: string,
): Promise<ResultatOuverture> {
  return requete('/auth/poste', {
    methode: 'POST',
    corps: { siteId },
    jeton: jetonTemporaire,
    schema: schemaResultatOuverture,
  })
}
